import React, { useState } from "react";
import Logo from "@/assets/Pitchster-logo.png";
import CustomInput from "@/components/CustomInput/CustomInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "@/hooks/useAuth";
import { isValid } from "@/utils/validators";

const SetNewPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { resetPassword, resetPasswordError } = useAuth();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNewPasswordError("");
    setConfirmPasswordError("");

    if (!newPassword.trim()) {
      setNewPasswordError("Please enter your new password");
      return;
    }

    if (!isValid("password", newPassword)) {
      setNewPasswordError(
        "Password must be at least 6 characters, include one uppercase letter, one lowercase letter, and one number",
      );
      return;
    }

    if (!confirmPassword.trim()) {
      setConfirmPasswordError("Please confirm your password");
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);

    resetPassword(
      { token, newPassword },
      {
        onSuccess: () => {
          toast.success("Password has been reset successfully");
          setTimeout(() => {
            setIsSubmitting(false);
            navigate("/login");
          }, 3000);
        },
        onError: () => {
          const msg = resetPasswordError?.message || "Password reset failed.";
          toast.error(msg);
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <div className="min-h-screen flex font-sans text-white bg-gradient-to-br from-[#0e002f] via-[#1d0340] to-[#0f001f]">
      <div className="w-full md:w-1/2 flex items-center justify-center px-5 py-6 bg-[#1a103f]">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center space-x-6 mb-6">
            <img src={Logo} alt="Logo" className="w-40 h-15" />
          </div>

          <h2 className="text-center text-2xl font-bold">Set New Password</h2>
          <p className="text-center text-gray-400 text-sm mb-4">
            Enter your new password below
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <CustomInput
              label="New Password"
              name="newPassword"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onBlur={() => {
                if (!newPassword.trim())
                  setNewPasswordError("Please enter your new password");
                else if (!isValid("password", newPassword))
                  setNewPasswordError(
                    "Password must be at least 6 characters, include one uppercase letter, one lowercase letter, and one number",
                  );
                else setNewPasswordError("");
              }}
              error={newPasswordError}
              required
            />

            <CustomInput
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => {
                if (!confirmPassword.trim())
                  setConfirmPasswordError("Please confirm your password");
                else if (confirmPassword !== newPassword)
                  setConfirmPasswordError("Passwords do not match");
                else setConfirmPasswordError("");
              }}
              error={confirmPasswordError}
              required
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 rounded-full cursor-pointer transition font-semibold ${
                isSubmitting
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }`}
            >
              {isSubmitting ? "Setting..." : "Set Password"}
            </button>

            <div className="text-center text-sm text-gray-300">
              <span
                className="hover:underline cursor-pointer"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </span>
            </div>
          </form>
        </div>
      </div>

      <div className="hidden md:flex w-1/2 items-center justify-center bg-gradient-to-tr from-[#1f005c] to-[#2c005e] relative overflow-hidden">
        <div className="absolute w-[300px] h-[300px] bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 opacity-30 rounded-full blur-3xl animate-pulse"></div>
        <div className="text-center z-10 px-10">
          <div className="flex justify-center mb-10">
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-4xl text-white shadow-lg">
              <Lock size={42} className="text-white" />
            </div>
          </div>
          <p className="text-gray-300 text-md max-w-md mx-auto">
            Choose a strong new password to keep your account safe and secure.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetNewPassword;
